import type { Options, RetryOptions } from 'ky';
import type { z } from 'zod';

import {
    wolfswapCookieApi,
    wolfswapFactionsApi,
} from '@/lib/api/clients/wolfswap';
import { isApiError } from '@/lib/errors';

import {
    isSnappyApiError,
    SnappyApiError,
    SnappySessionExpiredError,
} from './errors';
import {
    snappyAdmissionSchema,
    snappyBackupBlobResponseSchema,
    snappyDeleteBackupResponseSchema,
    snappyErrorBodySchema,
    snappyPutBackupResponseSchema,
    snappyUserSchema,
    voucherCheckSchema,
} from './schemas';
import type {
    SnappyAdmission,
    SnappyBackupBlobResponse,
    SnappyDeleteBackupResult,
    SnappyPutBackupResult,
    SnappyRequestOptions,
    SnappyUser,
    VoucherCheck,
} from './types';

/**
 * The factions launch-admission routes read the MoonHub.API social cookie
 * (relayed to `/v2/auth/session`), so they are credentialed, and they relay
 * `X-Snappy-Client` alongside it exactly like the `/v2/*` cookie client sends
 * it. factions mounts credentialed CORS for app.snappy.trade on
 * `/speed/snappy/admission*` only; every other factions route stays
 * cookie-free (see CLAUDE.md, "Two backends, one JWT").
 */
const factionsCookieApi = wolfswapFactionsApi.extend({
    credentials: 'include',
    headers: { 'X-Snappy-Client': 'web-v1' },
});

/** Answers worth asking again for: ky's own list, less 413, which ky never retries without a `Retry-After`. */
const RETRY_STATUSES: readonly number[] = [408, 429, 500, 502, 503, 504];

/**
 * The retry of every call here unless the caller sets one. The shared client's `beforeError` hook replaces ky's
 * `HTTPError` with an `ApiError` before ky decides whether to retry, so ky no longer sees the status and retries every
 * failed GET, a 401 or a 404 included (pinned by `src/lib/api/client.retry.test.ts`). The shared client stays as it
 * is (V1's request patterns are unchanged); these calls decide from the `ApiError`'s status instead, so a refusal
 * costs one request. Anything without a status (a network failure) keeps ky's default answer.
 */
const SNAPPY_RETRY: RetryOptions = {
    limit: 2,
    statusCodes: [408, 413, 429, 500, 502, 503, 504],
    shouldRetry: ({ error }) =>
        isApiError(error) ? RETRY_STATUSES.includes(error.status) : undefined,
};

function kyOptions(options: SnappyRequestOptions | undefined): Options {
    return {
        ...(options?.timeoutMs !== undefined
            ? { timeout: options.timeoutMs }
            : {}),
        retry: options?.retry ?? SNAPPY_RETRY,
        ...(options?.signal ? { signal: options.signal } : {}),
    };
}

/**
 * Every failure leaves here typed: 401 is {@link SnappySessionExpiredError},
 * an answered refusal is a {@link SnappyApiError} carrying the server's `code`
 * (its `error` text is kept as `detail`, for logs only), no answer at all is
 * status 0 / 'NETWORK', and an answer of the wrong shape is 'BAD_RESPONSE'.
 */
function toSnappyError(error: unknown): SnappyApiError {
    if (isSnappyApiError(error)) return error;
    if (isApiError(error)) {
        const body = snappyErrorBodySchema.safeParse(error.body);
        const code = body.success ? (body.data.code ?? null) : null;
        const detail = body.success ? (body.data.error ?? null) : null;
        if (error.status === 401) {
            return new SnappySessionExpiredError(code ?? 'NOT_AUTHENTICATED', {
                detail,
                cause: error,
            });
        }
        return new SnappyApiError(error.status, code, { detail, cause: error });
    }
    if (error instanceof SyntaxError) {
        // ky only parses a 2xx body, so the server answered with something
        // that is not JSON.
        return new SnappyApiError(200, 'BAD_RESPONSE', { cause: error });
    }
    return new SnappyApiError(0, 'NETWORK', { cause: error });
}

async function call<T>(
    send: () => Promise<unknown>,
    schema: z.ZodType<T>,
): Promise<T> {
    let data: unknown;
    try {
        data = await send();
    } catch (error) {
        throw toSnappyError(error);
    }
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
        throw new SnappyApiError(200, 'BAD_RESPONSE', { cause: parsed.error });
    }
    return parsed.data;
}

/**
 * `GET /v2/snappy/user`: the Snappy account behind the social session, with
 * its backup slots (metadata only). Creates the account row on first contact,
 * which is harmless: it holds no wallet until a backup is written.
 */
export async function fetchSnappyUser(
    options?: SnappyRequestOptions,
): Promise<SnappyUser> {
    return call(
        async () =>
            wolfswapCookieApi.get('v2/snappy/user', kyOptions(options)).json(),
        snappyUserSchema,
    );
}

/** `GET /factions/speed/snappy/admission` → `{ enabled, admitted }`. */
export async function fetchSnappyAdmission(
    options?: SnappyRequestOptions,
): Promise<SnappyAdmission> {
    return call(
        async () =>
            factionsCookieApi
                .get('speed/snappy/admission', kyOptions(options))
                .json(),
        snappyAdmissionSchema,
    );
}

/**
 * `POST /factions/speed/snappy/admission/redeem { code }`: admits the signed-in
 * identity to the browser wallet with a V2 launch code. With the launch switch
 * off it answers `{ enabled: false, admitted: true }` WITHOUT looking at the
 * code.
 */
export async function redeemSnappyAdmission(
    code: string,
    options?: SnappyRequestOptions,
): Promise<SnappyAdmission> {
    return call(
        async () =>
            factionsCookieApi
                .post('speed/snappy/admission/redeem', {
                    ...kyOptions(options),
                    json: { code },
                })
                .json(),
        snappyAdmissionSchema,
    );
}

/**
 * `POST /factions/speed/onboarding/voucher/check { code }` → `{ valid, flow }`.
 * Spends nothing and needs no identity, so no cookie is sent.
 */
export async function checkOnboardingVoucher(
    code: string,
    options?: SnappyRequestOptions,
): Promise<VoucherCheck> {
    return call(
        async () =>
            wolfswapFactionsApi
                .post('speed/onboarding/voucher/check', {
                    ...kyOptions(options),
                    json: { code },
                })
                .json(),
        voucherCheckSchema,
    );
}

/**
 * `GET /v2/snappy/backup/blob?address=` → `{ blob }`: the encrypted backup of
 * `address` (404 `NO_BACKUP` when the account holds none for it). The blob is
 * returned as received; parse it with the vendored engine.
 */
export async function getBackupBlob(
    address: string,
    options?: SnappyRequestOptions,
): Promise<SnappyBackupBlobResponse> {
    return call(
        async () =>
            wolfswapCookieApi
                .get('v2/snappy/backup/blob', {
                    ...kyOptions(options),
                    searchParams: { address },
                })
                .json(),
        snappyBackupBlobResponseSchema,
    );
}

/**
 * `PUT /v2/snappy/backup { blob, replace? }`: stores `blob` in the slot of its
 * wallet (adds one when the account holds none for it). Refusals: 403
 * `VOUCHER_REQUIRED` (an account with no slot, launch gate on), 409
 * `BACKUP_LIMIT` (100 slots, `MAX_BACKUPS`), 400 `BACKUP_INVALID`, 413.
 */
export async function putBackup(
    blob: object,
    replace?: string,
    options?: SnappyRequestOptions,
): Promise<SnappyPutBackupResult> {
    return call(
        async () =>
            wolfswapCookieApi
                .put('v2/snappy/backup', {
                    ...kyOptions(options),
                    json: replace === undefined ? { blob } : { blob, replace },
                })
                .json(),
        snappyPutBackupResponseSchema,
    );
}

/** `DELETE /v2/snappy/backup?address=`: removes the slot of `address`; idempotent. */
export async function deleteBackup(
    address: string,
    options?: SnappyRequestOptions,
): Promise<SnappyDeleteBackupResult> {
    return call(
        async () =>
            wolfswapCookieApi
                .delete('v2/snappy/backup', {
                    ...kyOptions(options),
                    searchParams: { address },
                })
                .json(),
        snappyDeleteBackupResponseSchema,
    );
}
